import PropTypes from "prop-types";
import React, { useMemo } from "react";
import { Layout, Grid as AntGrid, theme, Tooltip } from "antd";
import { useLocation, useNavigate } from "react-router-dom";
import { setChangeModule } from '@/store/reducers/useModule';
import { setChangeTestProd } from '@/store/reducers/testProdSelect';
import { resetFields } from '@/store/reducers/headerContent';
// project import
import HeaderContent from "./HeaderContent";
import AppBarStyled from "./AppBarStyled";
import { useSelector, useDispatch } from "react-redux";
import Breadcrumbs from "@/components/@extended/Breadcrumbs";
import { PageReturn } from "@/components/PageChrome";
import MenuItems from "@/menu-items/index";
import { useTheme } from '@/App';
import { getAccentColor } from '@/themes/antd-theme';
import { useMbmrAlias } from '@/hooks/useAppSetting';

const { Header: AntHeader } = Layout;
const { useBreakpoint } = AntGrid;

// ==============================|| MAIN LAYOUT - HEADER ||============================== //

const Header = ({ open, handleDrawerToggle }) => {
  const { isDark } = useTheme();
  const mbmrAlias = useMbmrAlias();
  const { token } = theme.useToken();
  const screens = useBreakpoint();
  const matchDownMD = !screens.lg;
  const navigation = MenuItems();
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const { theme_color } = useSelector((state) => state.company_profile);
  const buttonBgColor = getAccentColor(theme_color, isDark);
  const browserPath = location?.pathname;
  const selectedModule = useSelector((state) => state.moduleView);
  const selectedTestProd = useSelector((state) => state.testProdView);
  const moduleChange = selectedModule === "prod" ? "pack" : "prod";
  const finalPrModule = selectedModule === "prod" ? "MBMR" : "MBPR";
  const headerContent = useSelector((state) => state.headerContent);
  const allowedPaths = ["/recipeMetadata", "/recipe-view", "/edit-process"];

  // 2026-09-28 · the plant, named on the header.
  //
  // Every request already carries it — `getActivePlantId()` reads this same key for the
  // plant_id it sends — but nothing on screen said WHICH plant the page was about. On a
  // one-plant estate that reads as a missing fact; on a two-plant one it is the difference
  // between looking at your own areas and somebody else's. Read, not chosen: switching
  // plants is a separate act and belongs to whatever screen owns it.
  const activePlant = useMemo(() => {
    try {
      const raw = localStorage.getItem("active-plant");
      if (!raw || raw === "null" || raw === "undefined") return null;
      const p = JSON.parse(raw);
      return p?.plant_name || p?.name || p?.plant_code || null;
    } catch {
      return null;
    }
  }, []);

  const HEADER_LABEL_MAX_CHARS = 25;
  const truncateLabel = (text) =>
    typeof text === "string" && text.length > HEADER_LABEL_MAX_CHARS
      ? `${text.slice(0, HEADER_LABEL_MAX_CHARS)}...`
      : text;

  const HeaderInfo = ({ data = headerContent }) => {
    if (!headerContent || Object?.keys(headerContent)?.length === 0) {
      return (
        <Breadcrumbs
          navigation={navigation}
          title
          titleBottom
          card={false}
          divider={false}
        />
      );
    } else {
      if (
        (allowedPaths?.includes(browserPath) && !headerContent?.Process) ||
        browserPath?.startsWith("/process-config") ||
        // The record designer hosts the same process tree as /process-config and sets the
        // same two title fields. Without this line the else-branch below reset them on
        // every render, so the tree's header read "-" while the store had just been written.
        browserPath?.startsWith("/record-designer")
      ) {
        return (
          <div className="flex items-center gap-12 text-sm">
            {/* `_mbmrStatus` is not a crumb of its own: it is the record's stage, drawn beside the MBMR name. */}
            {Object?.entries(data)?.filter(([key]) => key !== "_mbmrStatus")?.map(([key, value], index) => (
              <div
                key={key}
                className={`${index !== 0 ? "border-l pl-4" : ""}`}
              >
                <span className="text-gray-500">{key === 'MBMR' ? mbmrAlias : key}</span>
                <div className="font-semibold">
                  {typeof value === "object" ? (
                    <>
                      <Tooltip title={value.text}>
                        <span>{truncateLabel(value.text)}</span>
                      </Tooltip>
                      {value.status && (
                        <span
                          className={`ml-2 text-xs px-2 py-0.5 rounded-md border
                      ${
                        value.status === "Running"
                          ? "bg-orange-100 text-orange-600 border-orange-300"
                          : value.status === "Completed"
                          ? "bg-green-100 text-green-600 border-green-300"
                          : "bg-gray-100 text-gray-600 border-gray-300"
                      }`}
                        >
                          {value.status}
                        </span>
                      )}
                    </>
                  ) : (
                    <>
                      <Tooltip title={value}>
                        <span className="font-bold">{truncateLabel(value)}</span>
                      </Tooltip>
                      {/* 2026-10-28 (owner) · the record designer's stage (Draft, Under review, Approved …),
                          here instead of a line of its own above the panes. */}
                      {key === "MBMR" && data._mbmrStatus?.text ? (
                        <Tooltip title={data._mbmrStatus.note || undefined}>
                          <span
                            className={`ml-2 text-xs px-2 py-0.5 rounded-md border font-semibold ${
                              data._mbmrStatus.tone === "ok"
                                ? "bg-green-100 text-green-700 border-green-300"
                                : data._mbmrStatus.tone === "warn"
                                ? "bg-orange-100 text-orange-700 border-orange-300"
                                : "bg-gray-100 text-gray-700 border-gray-300"
                            }`}
                          >
                            {data._mbmrStatus.text}
                          </span>
                        </Tooltip>
                      ) : null}
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        );
      } else {
        dispatch(resetFields());
        return (
          <Breadcrumbs
            navigation={navigation}
            title
            titleBottom
            card={false}
            divider={false}
          />
        );
      }
    }
  };

  // common header
  const mainHeader = (
    <div className={`px-6 py-0 h-16 flex items-center ${isDark ? 'bg-gray-800' : 'bg-white'}`} style={{ lineHeight: 1.5 }}>
      <div className="flex justify-between items-center w-full">
        <div className="flex-1">
          <div className={`w-full ${isDark ? 'bg-gray-800' : 'bg-white'}`}>
            <div className="flex items-center gap-6">
              {/* Batch / Process / Operation / Batch Size */}
              <HeaderInfo />
            </div>
          </div>
        </div>

        {/* <div>
          <div className="flex items-center justify-center gap-2">
            <div>
              <img
                alt="Company Logo"
                src="https://png.pngtree.com/png-vector/20190304/ourmid/pngtree-growth-business-company-logo-png-image_728232.jpg"
                style={{ width: 42, height: 42, borderRadius: "50%" }}
              />
            </div>
            <div>
              <div className="text-lg font-semibold">
                Nexgensis
              </div>
              <div className="text-xs text-gray-500">
                Pune, Maharashtra
              </div>
            </div>
          </div>
        </div> */}

        <div>
          <div className="flex gap-2 items-center">
            <div className="flex gap-2 items-center">
              {activePlant ? (
                <span
                  className="text-sm"
                  style={{ color: isDark ? "rgba(255,255,255,0.65)" : "rgba(0,0,0,0.55)" }}
                  title="The plant this session is working in"
                >
                  {activePlant}
                </span>
              ) : null}
              {/* MBMR / MBPR module switch — manufacturing vs packaging records (same shape as Test/Production) */}
              <div
                className="inline-flex items-center rounded-md p-0.5 select-none"
                style={{
                  height: 32,
                  backgroundColor: isDark ? "rgba(255,255,255,0.10)" : "rgba(0,0,0,0.06)",
                  border: `1px solid ${isDark ? "rgba(255,255,255,0.15)" : "rgba(0,0,0,0.08)"}`,
                }}
                role="tablist"
                aria-label="Manufacturing (MBMR) or Packaging (MBPR) records"
              >
                {[
                  { key: "prod", label: "MBMR" },
                  { key: "pack", label: "MBPR" },
                ].map((opt) => {
                  const active = selectedModule === opt.key;
                  return (
                    <Tooltip key={opt.key} title={opt.key === "prod" ? "Manufacturing records (BMR)" : "Packaging records (BPR)"} placement="bottom" zIndex={9999}>
                      <button
                        role="tab"
                        aria-selected={active}
                        onClick={() => { if (!active) dispatch(setChangeModule(opt.key)); }}
                        className="px-3 h-full rounded text-xs font-semibold transition-colors"
                        style={{
                          backgroundColor: active ? buttonBgColor : "transparent",
                          color: active ? "#fff" : (isDark ? "rgba(255,255,255,0.75)" : "rgba(0,0,0,0.65)"),
                          border: "none", cursor: active ? "default" : "pointer",
                        }}
                      >
                        {opt.label}
                      </button>
                    </Tooltip>
                  );
                })}
              </div>
              {/* Trial / Production segmented switcher — both states visible,
                  the active one filled with the accent color. Square style. */}
              <div
                className="inline-flex items-center rounded-md p-0.5 select-none"
                style={{
                  height: 32,
                  backgroundColor: isDark ? "rgba(255,255,255,0.10)" : "rgba(0,0,0,0.06)",
                  border: `1px solid ${isDark ? "rgba(255,255,255,0.15)" : "rgba(0,0,0,0.08)"}`,
                }}
                role="tablist"
                aria-label="Trial or Production mode"
              >
                {[
                  { key: "test", label: "Trial" },
                  { key: "production", label: "Production" },
                ].map((opt) => {
                  const active = selectedTestProd === opt.key;
                  return (
                    <button
                      key={opt.key}
                      type="button"
                      role="tab"
                      aria-selected={active}
                      onClick={() => {
                        if (!active) {
                          dispatch(setChangeTestProd(opt.key));
                          // Toggling the mode routes back to the recipe list.
                          navigate("/recipe-view");
                        }
                      }}
                      className="px-3.5 h-full flex items-center rounded text-sm font-medium transition-all duration-200"
                      style={{
                        backgroundColor: active ? buttonBgColor : "transparent",
                        color: active
                          ? "#ffffff"
                          : isDark ? "rgba(255,255,255,0.7)" : "rgba(0,0,0,0.6)",
                        boxShadow: active ? "0 1px 4px rgba(0,0,0,0.2)" : "none",
                        cursor: active ? "default" : "pointer",
                      }}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            </div>
            <HeaderContent />
            {/* The page's way back used to be a "←" here (components/PageChrome/PageReturn). Back now
                lives in every page header (components/PageHeader), so the bar no longer draws it. */}
            {/* <PageReturn /> */}
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <>
      {!matchDownMD ? (
        <AppBarStyled
          open={open}
          style={{
            borderBottom: `1px solid ${isDark ? token.colorBorderSecondary : token.colorBorder}`,
          }}
        >
          {mainHeader}
        </AppBarStyled>
      ) : (
        <AntHeader
          className="fixed z-[1200]"
          style={{
            padding: 0,
            height: 64,
            background: isDark ? token.colorBgContainer : '#fff',
            borderBottom: `1px solid ${isDark ? token.colorBorderSecondary : token.colorBorder}`,
          }}
        >
          {mainHeader}
        </AntHeader>
      )}
    </>
  );
};

Header.propTypes = {
  open: PropTypes.bool,
  handleDrawerToggle: PropTypes.func,
};

export default Header;
